import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = logged in
  const [token, setToken] = useState(() => localStorage.getItem("ehp_token"));

  const loadMe = useCallback(async () => {
    const t = localStorage.getItem("ehp_token");
    if (!t) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("ehp_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const applyAuth = (data) => {
    localStorage.setItem("ehp_token", data.token);
    setToken(data.token);
    setUser(data.user);
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiErr(e, "Invalid email or password") };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      applyAuth(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiErr(e, "Registration failed") };
    }
  };

  const googleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      applyAuth(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiErr(e, "Google sign-in failed") };
    }
  };

  const logout = () => {
    localStorage.removeItem("ehp_token");
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, googleSession, logout, isAdmin: user && user.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
